(ns heraldicon.heraldry.charge.shared
  (:require
   [heraldicon.context :as c]
   [heraldicon.heraldry.field.environment :as environment]
   [heraldicon.heraldry.line.fimbriation :as fimbriation]
   [heraldicon.heraldry.option.position :as position]
   [heraldicon.interface :as interface]
   [heraldicon.math.bounding-box :as bb]
   [heraldicon.math.core :as math]
   [heraldicon.math.vector :as v]
   [heraldicon.options :as options]
   [heraldicon.svg.path :as path]
   [heraldicon.svg.path-data :as path-data]
   [heraldicon.svg.squiggly :as squiggly]))

(def ^:private outline-mode-choices
  [[:string.option.outline-mode-choice/keep :keep]
   [:string.option.outline-mode-choice/transparent :transparent]
   [:string.option.outline-mode-choice/primary :primary]
   [:string.option.outline-mode-choice/remove :remove]])

(def outline-mode-map
  (options/choices->map outline-mode-choices))

(defn options [context]
  (let [anchor-point-option {:type :option.type/choice
                             :choices (position/anchor-choices
                                       [:fess
                                        :chief
                                        :base
                                        :dexter
                                        :sinister
                                        :honour
                                        :nombril
                                        :hoist
                                        :fly
                                        :top-left
                                        :top
                                        :top-right
                                        :left
                                        :center
                                        :right
                                        :bottom-left
                                        :bottom
                                        :bottom-right])
                             :default :fess
                             :ui/label :string.option/point}
        orientation-point-option {:type :option.type/choice
                                  :choices (position/orientation-choices
                                            [:top-left
                                             :top
                                             :top-right
                                             :left
                                             :center
                                             :right
                                             :bottom-left
                                             :bottom
                                             :bottom-right
                                             :fess
                                             :chief
                                             :base
                                             :dexter
                                             :sinister
                                             :honour
                                             :nombril
                                             :angle])
                                  :default :angle
                                  :ui/label :string.option/orientation}
        current-orientation-point (options/get-value
                                   (interface/get-raw-data (c/++ context :orientation :point))
                                   orientation-point-option)
        ornament? (some #{:ornaments} (:path context))
        offset-distance (if ornament?
                          200
                          45)]
    {:adapt-to-ordinaries? {:type :option.type/boolean
                            :default true
                            :ui/label :string.option/adapt-to-ordinaries?}
     :anchor {:point anchor-point-option
              :offset-x {:type :option.type/range
                         :min (- offset-distance)
                         :max offset-distance
                         :default 0
                         :ui/label :string.option/offset-x
                         :ui/step 0.1}
              :offset-y {:type :option.type/range
                         :min (- offset-distance)
                         :max offset-distance
                         :default 0
                         :ui/label :string.option/offset-y
                         :ui/step 0.1}
              :ui/label :string.option/anchor
              :ui/element :ui.element/position}
     :orientation (cond-> {:point orientation-point-option
                           :ui/label :string.option/orientation
                           :ui/element :ui.element/position}

                    (= current-orientation-point
                       :angle) (assoc :angle {:type :option.type/range
                                              :min -180
                                              :max 180
                                              :default 0
                                              :ui/label :string.option/angle})

                    (not= current-orientation-point
                          :angle) (assoc :offset-x {:type :option.type/range
                                                    :min (- offset-distance)
                                                    :max offset-distance
                                                    :default 0
                                                    :ui/label :string.option/offset-x
                                                    :ui/step 0.1}
                                         :offset-y {:type :option.type/range
                                                    :min (- offset-distance)
                                                    :max offset-distance
                                                    :default 0
                                                    :ui/label :string.option/offset-y
                                                    :ui/step 0.1}))
     :geometry {:size {:type :option.type/range
                       :min 5
                       :max 250
                       :default 50
                       :default-display-value "auto"
                       :ui/label :string.option/size
                       :ui/step 0.1}
                :stretch {:type :option.type/range
                          :min 0.1
                          :max 10
                          :default 1
                          :ui/label :string.option/stretch
                          :ui/step 0.01}
                :mirrored? {:type :option.type/boolean
                            :default false
                            :ui/label :string.option/mirrored?}
                :reversed? {:type :option.type/boolean
                            :default false
                            :ui/label :string.option/reversed?}
                :ui/label :string.option/geometry
                :ui/element :ui.element/geometry}
     :fimbriation (-> (fimbriation/options (c/++ context :fimbriation))
                      (dissoc :alignment)
                      (options/override-if-exists [:corner :default] :round)
                      (options/override-if-exists [:thickness-1 :max] 25)
                      (options/override-if-exists [:thickness-1 :default] 2.5)
                      (options/override-if-exists [:thickness-2 :max] 25)
                      (options/override-if-exists [:thickness-2 :default] 1.5))
     :outline-mode {:type :option.type/choice
                    :choices outline-mode-choices
                    :default :keep
                    :ui/label :string.option/outline-mode}
     :horizontal-mask {:type :option.type/range
                       :default 0
                       :min -100
                       :max 100
                       :ui/label :string.option/horizontal-mask
                       :ui/step 1}
     :vertical-mask {:type :option.type/range
                     :default 0
                     :min -100
                     :max 100
                     :ui/label :string.option/vertical-mask
                     :ui/step 1}}))

(defn apply-horizontal-mask [context {:keys [bounding-box width height]
                                      :as properties}]
  (let [horizontal-mask (interface/get-sanitized-data (c/++ context :horizontal-mask))
        {:keys [min-x max-x
                min-y max-y]} bounding-box
        unmasked-width (- max-x min-x)
        mask-width (math/percent-of unmasked-width (Math/abs horizontal-mask))
        [min-x max-x] (if (pos? horizontal-mask)
                        [min-x (max min-x (- max-x mask-width))]
                        [(min max-x (+ min-x mask-width)) max-x])
        bounding-box (assoc bounding-box
                            :min-x min-x
                            :max-x max-x)
        [real-width real-height] (bb/size bounding-box)
        horizontal-mask-shape (when-not (zero? horizontal-mask)
                                (let [fimbriation-percentage-base (min width height)
                                      {:keys [mode
                                              thickness-1
                                              thickness-2]} (some-> (interface/get-sanitized-data (c/++ context :fimbriation))
                                                                    (update :thickness-1 (partial math/percent-of fimbriation-percentage-base))
                                                                    (update :thickness-2 (partial math/percent-of fimbriation-percentage-base)))
                                      margin (+ 3
                                                (case mode
                                                  :double (+ thickness-1 thickness-2)
                                                  :single thickness-1
                                                  0))
                                      [mask-min-y mask-max-y] [(- min-y margin) (+ max-y margin)]
                                      [mask-min-x mask-max-x] (if (pos? horizontal-mask)
                                                                [(- min-x margin) max-x]
                                                                [min-x (+ max-x margin)])]
                                  (path/make-path
                                   ["M" mask-min-x mask-min-y
                                    "H" mask-max-x
                                    "V" mask-max-y
                                    "H" mask-min-x
                                    "z"])))]
    (assoc properties
           :bounding-box bounding-box
           :width real-width
           :height real-height
           :horizontal-mask-shape horizontal-mask-shape)))

(defn apply-vertical-mask [context {:keys [bounding-box width height]
                                    :as properties}]
  (let [vertical-mask (interface/get-sanitized-data (c/++ context :vertical-mask))
        {:keys [min-x max-x
                min-y max-y]} bounding-box
        unmasked-height (- max-y min-y)
        mask-height (math/percent-of unmasked-height (Math/abs vertical-mask))
        [min-y max-y] (if (pos? vertical-mask)
                        [min-y (max min-y (- max-y mask-height))]
                        [(min max-y (+ min-y mask-height)) max-y])
        bounding-box (assoc bounding-box
                            :min-y min-y
                            :max-y max-y)
        [real-width real-height] (bb/size bounding-box)
        vertical-mask-shape (when-not (zero? vertical-mask)
                              (let [fimbriation-percentage-base (min width height)
                                    {:keys [mode
                                            thickness-1
                                            thickness-2]} (some-> (interface/get-sanitized-data (c/++ context :fimbriation))
                                                                  (update :thickness-1 (partial math/percent-of fimbriation-percentage-base))
                                                                  (update :thickness-2 (partial math/percent-of fimbriation-percentage-base)))
                                    margin (+ 3
                                              (case mode
                                                :double (+ thickness-1 thickness-2)
                                                :single thickness-1
                                                0))
                                    [mask-min-x mask-max-x] [(- min-x margin) (+ max-x margin)]
                                    [mask-min-y mask-max-y] (if (pos? vertical-mask)
                                                              [(- min-y margin) max-y]
                                                              [min-y (+ max-y margin)])]
                                (path/make-path
                                 ["M" mask-min-x mask-min-y
                                  "H" mask-max-x
                                  "V" mask-max-y
                                  "H" mask-min-x
                                  "z"])))]
    (assoc properties
           :bounding-box bounding-box
           :width real-width
           :height real-height
           :vertical-mask-shape vertical-mask-shape)))

(defn process-shape [{:keys [size-default
                             auto-resize?]
                      :or {auto-resize? true}
                      :as context}
                     {:keys [base-shape base-width base-height]
                      :as base-properties}]
  (let [{:keys [width height points]
         :as parent-environment} (interface/get-parent-field-environment context)
        {:keys [left right top bottom]} points
        anchor (interface/get-sanitized-data (c/++ context :anchor))
        orientation (interface/get-sanitized-data (c/++ context :orientation))
        size (if (interface/get-raw-data (c/++ context :geometry :size))
               (interface/get-sanitized-data (c/++ context :geometry :size))
               (if auto-resize?
                 size-default
                 nil))
        stretch (interface/get-sanitized-data (c/++ context :geometry :stretch))
        mirrored? (interface/get-sanitized-data (c/++ context :geometry :mirrored?))
        reversed? (interface/get-sanitized-data (c/++ context :geometry :reversed?))
        squiggly? (interface/render-option :squiggly? context)
        anchor-override (c/get-key context :anchor-override)
        {:keys [slot-spacing
                slot-angle]} (c/get-key context :charge-group)
        environment-for-anchor (if anchor-override
                                 (assoc-in parent-environment [:points :special] anchor-override)
                                 parent-environment)
        anchor (if anchor-override
                 {:point :special
                  :offset-x 0
                  :offset-y 0}
                 anchor)
        {anchor-point :real-anchor
         orientation-point :real-orientation} (position/calculate-anchor-and-orientation
                                               environment-for-anchor
                                               anchor
                                               orientation
                                               0
                                               -90)
        angle (+ (v/angle-to-point anchor-point orientation-point)
                 90)
        min-x-distance (or (some-> slot-spacing :width (/ 2) (/ 0.9))
                           (min (- (:x anchor-point) (:x left))
                                (- (:x right) (:x anchor-point))))
        min-y-distance (or (some-> slot-spacing :height (/ 2) (/ 0.8))
                           (min (- (:y anchor-point) (:y top))
                                (- (:y bottom) (:y anchor-point))))
        target-width (if size
                       (math/percent-of width size)
                       (* min-x-distance 2 0.8))
        target-height (/ (if size
                           (math/percent-of height size)
                           (* min-y-distance 2 0.7))
                         stretch)
        angle (if (and (-> orientation :point (= :angle))
                       slot-angle)
                (if (< base-height base-width)
                  (+ angle slot-angle 90)
                  (+ angle slot-angle))
                angle)
        scale-x (* (if mirrored? -1 1)
                   (min (/ target-width base-width)
                        (/ target-height base-height)))
        scale-y (* (if reversed? -1 1)
                   (Math/abs scale-x)
                   stretch)
        base-top-left (v/div (v/Vector. base-width base-height)
                             -2)
        ;; Build the scaled charge in its own un-rotated, un-positioned frame.
        ;; Masking is computed in this frame so that it follows the charge's own
        ;; axes; the shape, mask shapes, and bounding box are then rotated and
        ;; moved into place together. Otherwise an axis-aligned mask would be
        ;; applied after the rotation, which is unintuitive for charges that are
        ;; rotated, e.g. to face around a charge group.
        ;; paths stay data until they're written out once at the end, see
        ;; heraldicon.svg.path-data; only squiggly works on the written path
        scaled-shape (into []
                           (map #(-> %
                                     path/make-path
                                     path-data/parse
                                     (path-data/scale scale-x scale-y)
                                     (cond->
                                       squiggly? (->
                                                   path-data/to-svg
                                                   squiggly/squiggly-path
                                                   path-data/parse))))
                           base-shape)
        local-bounding-box (bb/from-path-data scaled-shape)
        rotation-center (bb/center local-bounding-box)
        place (fn [path]
                (-> (if (string? path)
                      (path-data/parse path)
                      path)
                    (cond-> (not= angle 0) (path-data/rotate angle rotation-center))
                    (path-data/translate (:x anchor-point) (:y anchor-point))
                    path-data/to-svg))
        {masked-bounding-box :bounding-box
         :keys [horizontal-mask-shape
                vertical-mask-shape]} (->> (merge base-properties
                                                  {:bounding-box local-bounding-box
                                                   :width (bb/width local-bounding-box)
                                                   :height (bb/height local-bounding-box)})
                                           (apply-horizontal-mask context)
                                           (apply-vertical-mask context))
        bounding-box (-> (path/make-path
                          ["M" (:min-x masked-bounding-box) (:min-y masked-bounding-box)
                           "H" (:max-x masked-bounding-box)
                           "V" (:max-y masked-bounding-box)
                           "H" (:min-x masked-bounding-box)
                           "z"])
                         place
                         vector
                         bb/from-paths)]

    (merge base-properties
           {:type (interface/get-raw-data (c/++ context :type))
            :bounding-box bounding-box
            :width (bb/width bounding-box)
            :height (bb/height bounding-box)
            :shape (mapv place scaled-shape)
            :anchor-point anchor-point
            :scale-x scale-x
            :scale-y scale-y
            :angle angle
            :top-left base-top-left
            :horizontal-mask-shape (some-> horizontal-mask-shape place)
            :vertical-mask-shape (some-> vertical-mask-shape place)})))

(defmethod interface/environment :heraldry/charge [context]
  (let [{:keys [bounding-box anchor-point]} (interface/get-properties context)]
    (environment/create bounding-box {:fess anchor-point})))

(defmethod interface/bounding-box :heraldry/charge [context]
  (:bounding-box (interface/get-properties context)))

(defmethod interface/render-shape :heraldry/charge [context]
  {:shape (:shape (interface/get-properties context))})
